import { Trash2 } from 'lucide-react';
import NumField from './NumField';

/**
 * Properties panel for the currently-selected landmark.
 *
 * Extracted from MiniMapEditor.js to keep the parent < 800 lines and to isolate
 * the "single-landmark inspector" concern.
 *
 * Props:
 *   landmark          — the selected landmark object (has id, type, label, width, ...)
 *   onSave(patched)   — persist an updated landmark
 *   onDelete(id?)     — optional; if provided, shows a Delete button
 *   onDeselect()      — clear the selection (closes the panel)
 */
export default function LandmarkPropertiesPanel({ landmark, onSave, onDelete, onDeselect }) {
  if (!landmark) return null;
  const isRect = ['stadium', 'carpark', 'tree_line', 'building'].includes(landmark.type);
  const patch = (delta) => onSave({ ...landmark, ...delta });

  return (
    <div
      className="rounded-xl border border-sky-500/40 bg-slate-900/60 p-3 flex flex-wrap items-end gap-3"
      data-testid="landmark-properties-panel"
    >
      <div className="flex-1 min-w-[140px]">
        <div className="text-[10px] uppercase font-black tracking-widest text-sky-400 mb-1">
          Selected · {landmark.type}
        </div>
        <input
          value={landmark.label || ''}
          onChange={(e) => patch({ label: e.target.value })}
          onBlur={(e) => patch({ label: e.target.value })}
          className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white text-xs"
          placeholder="Label"
          data-testid="landmark-label-input"
        />
      </div>

      {isRect ? (
        <>
          <NumField label="Width" value={landmark.width} onChange={(v) => patch({ width: v })} testid="landmark-width-input" />
          <NumField label="Depth" value={landmark.depth} onChange={(v) => patch({ depth: v })} testid="landmark-depth-input" />
          <NumField label="Height" value={landmark.height} onChange={(v) => patch({ height: v })} testid="landmark-height-input" />
        </>
      ) : (
        <NumField label="Scale" value={landmark.scale} step={0.1} onChange={(v) => patch({ scale: v })} testid="landmark-scale-input" />
      )}

      <NumField label="Rot°" value={landmark.rotation} onChange={(v) => patch({ rotation: v })} testid="landmark-rotation-input" />

      <div>
        <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">Color</div>
        <input
          type="color"
          value={landmark.color || '#94a3b8'}
          onChange={(e) => patch({ color: e.target.value })}
          className="w-10 h-8 bg-transparent border border-slate-700 rounded cursor-pointer"
          data-testid="landmark-color-input"
        />
      </div>

      {onDelete && (
        <button
          onClick={() => { onDelete(landmark.id); onDeselect(); }}
          className="ml-auto px-3 py-2 rounded-lg bg-red-500/80 hover:bg-red-600 text-white text-xs font-bold flex items-center gap-1.5"
          data-testid="delete-landmark-btn"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      )}
      <button
        onClick={onDeselect}
        className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold"
        data-testid="deselect-landmark-btn"
      >
        Close
      </button>
    </div>
  );
}
